import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Table,
  InputGroup,
  OverlayTrigger,
  Popover,
  Tooltip,
  Modal,
} from "react-bootstrap";
import * as Yup from "yup";
import { RotatingLines } from "react-loader-spinner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import url from "../../../configs/config";
import Card from "../../../components/Card";
import Select from "react-select";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import { IoEyeOutline } from "react-icons/io5";
import FadeLoaderComponent from "../../../components/loader-component";
import SearchInput from "../../../components/search-input";
import PaginationComponent from "../../../components/pagination/pagination-component";
import * as SettingSelector from "../../../store/setting/selectors";
import { useSelector } from "react-redux";
import { ErrorMessage, Field, Formik } from "formik";
import { roleTypes } from "../../../data/roleType";
import { useLocation } from "react-router-dom";
import { IconButton } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";

const RequestProductList = () => {
  const alertRef = useRef(null);
  const [successStatus, setSuccessStatus] = useState({});
  const [failStatus, setFailStatus] = useState({});
  const [productRequestId, setProductRequestId] = useState(null);
  const [productRequests, setProductRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteIsSubmit, setDeleteIsSubmit] = useState(true);
  const [dataLoader, setDataLoader] = useState(true);
  const [selectedData, setSelectedData] = useState();
  const [decodedToken, setDecodedToken] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("coyote-token");
  const permissions = useSelector(SettingSelector.set_permissions);
  const apiCallInProgress = useRef(false);
  const initialLoadComplete = useRef(false);
  const abortControllerRef = useRef(null);
  const [highlightedRequestId, setHighlightedRequestId] = useState(null);
  const location = useLocation();
  const hasViewPermission = permissions?.product_request?.view === true;
  const hasUpdatePermission = permissions?.product_request?.update === true;
  const hasDeletePermission = permissions?.product_request?.delete === true;
  const hasCreatePermission = permissions?.product_request?.create === true;
  const hasStatusPermission = permissions?.product_request?.status === true;
  const orderHasCreatePermission = permissions?.purchase_order?.create === true;
  const orderHasViewPermission = permissions?.purchase_order?.view === true;
  const orderHasUpdatePermission = permissions?.purchase_order?.update === true;
  const orderHasDeletePermission = permissions?.purchase_order?.delete === true;
  const orderHasStatusPermission = permissions?.purchase_order?.status === true;
  const warehouseHasCreatePermission = permissions?.warehouse?.create === true;

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getProductRequests = async (offset = 0, limit = 10) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (apiCallInProgress.current) return;
    apiCallInProgress.current = true;

    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      setDataLoader(false);
      return;
    } else {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
        params: {
          warehouse: decodedToken?.warehouse_id,
          offset,
          limit,
          search:
            searchQuery.trim().length >= 2
              ? searchQuery.trim()
              : searchQuery.trim().length < 1
                ? null
                : null,
        },
        signal: controller.signal,
      };

      try {
        const response = await axios.get(
          `${url.backendURL}product-request/`,
          config,
        );

        if (!controller.signal.aborted) {
          setProductRequests(response.data.response.data);
          setTotalEntries(response.data.response.productRequestsCount);
          setDataLoader(false);
        }
      } catch (err) {
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
          return;
        }

        if (alertRef?.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        handleError(err, "product-requests");
        setDataLoader(false);
      } finally {
        apiCallInProgress.current = false;
        initialLoadComplete.current = true;
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        },
      );
    } else {
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  useEffect(() => {
    if (!decodedToken) return;
    if (!initialLoadComplete.current) {
      getProductRequests((currentPage - 1) * entriesPerPage, entriesPerPage);
    } else if (!apiCallInProgress.current) {
      getProductRequests((currentPage - 1) * entriesPerPage, entriesPerPage);
    }
  }, [currentPage, entriesPerPage, decodedToken]);

  useEffect(() => {
    if (!initialLoadComplete.current) return;
    const debounceTimer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2 || searchQuery.trim().length < 1) {
        setLoading(true);
        setCurrentPage(1);
        await getProductRequests(0, entriesPerPage);
        setLoading(false);
      }
    }, 300);
    return () => {
      clearTimeout(debounceTimer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (location.state?.showSuccessMessage && location.state?.successMessage) {
      setAlert({
        message: location.state.successMessage,
        type: "success",
        show: true,
        alertFor: "product-requests",
      });

      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);

      window.history.replaceState({}, document.title);
    }

    if (location.state?.productRequestId && productRequests.length > 0) {
      const requestIdToHighlight = location.state.productRequestId;
      setHighlightedRequestId(requestIdToHighlight);

      setTimeout(() => {
        const highlightedRow = document.querySelector(
          `[data-request-id="${requestIdToHighlight}"]`,
        );
        if (highlightedRow) {
          highlightedRow.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      setTimeout(() => {
        setHighlightedRequestId(null);
        window.history.replaceState({}, document.title);
      }, 5000);
    }
  }, [location.state, productRequests]);

  const handleDeleteProductRequest = async (e, id) => {
    e.preventDefault();
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const response = await axios.delete(
        `${url.backendURL}product-request/${id}`,
        config,
      );

      await getProductRequests(
        (currentPage - 1) * entriesPerPage,
        entriesPerPage,
      );

      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "product-requests",
      });
      setShowPopover(null);
      setDeleteIsSubmit(true);

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } catch (err) {
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "product-requests");
      setDeleteIsSubmit(true);
      setShowPopover(null);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (totalEntries === 0) return;
    setCurrentPage(pageNumber);
  };

  const handleEntriesPerPageChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const [showPopover, setShowPopover] = useState(null);

  const togglePopover = (idx) => {
    if (showPopover === idx) {
      setShowPopover(null);
    } else {
      setShowPopover(idx);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (showPopover !== null) {
        setShowPopover(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showPopover]);

  const [selectedProductRequest, setSelectedProductRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const statusOptions = ["Pending", "Approved", "Rejected"];
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showItemsInModal, setShowItemsInModal] = useState(false);
  const [orderModalData, setOrderModalData] = useState({
    poNumber: "",
    warehouseId: null,
    vendorId: null,
    shipToAddress: "",
    items: [],
  });

  const getStatusOptions = () => {
    if (
      decodedToken?.roles?.some(
        (role) =>
          role.role_type === roleTypes.ADMIN ||
          role.role_type === roleTypes.PURCHASING ||
          role.role_type === roleTypes.SUPPLIER,
      ) &&
      orderHasCreatePermission
    ) {
      return ["Pending", "Approved", "Rejected", "Create Order"];
    }
    return ["Pending", "Approved", "Rejected"];
  };

  const statusOptionsFormatted = getStatusOptions().map((status) => ({
    value: status.toLowerCase().replace(" ", "_"),
    label: status,
  }));

  const [pendingOrderPRId, setPendingOrderPRId] = useState(null);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingPRId, setLoadingPRId] = useState(null);

  const orderValidationSchema = Yup.object().shape({
    poNumber: Yup.string()
      .trim()
      .min(3, "PO # must be at least 3 characters")
      .matches(
        /^[A-Za-z0-9-]+$/,
        "PO # can only contain alphanumeric characters and hyphens",
      )
      .required("PO # is required"),
    warehouseId: Yup.number()
      .integer("Warehouse is invalid")
      .positive("Warehouse is invalid")
      .required("Warehouse is required"),
    vendorId: Yup.number()
      .integer("Vendor is invalid")
      .positive("Vendor is invalid")
      .required("Vendor is required"),
    shipToAddress: Yup.string()
      .trim()
      .min(2, "Ship To address must be at least 2 characters")
      .max(255, "Ship To address must not exceed 255 characters")
      .matches(
        /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
        "Ship To address must contain at least one alphanumeric character",
      )
      .required("Ship To address is required"),
    items: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number().nullable(),
          productItemId: Yup.number().nullable(),
          createOrder: Yup.boolean().nullable(),
        }),
      )
      .test(
        "at-least-one-item",
        "At least one item must be selected for order",
        function (items) {
          if (!items || items.length === 0) return false;
          const selectedItems = items.filter(
            (item) => item.createOrder !== false,
          );
          return selectedItems.length > 0;
        },
      )
      .min(1, "At least one item is required")
      .required("At least one item is required"),
  });

  const handleSaveProductRequestFieldSelect = async (id, value, notes) => {
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      let data = {
        status: value,
      };
      if (notes) {
        data = {
          status: value,
          notes: notes,
        };
      }
      await axios.put(
        `${url.backendURL}product-request/${id}/status`,
        data,
        config,
      );

      setIsLoading(false);
      setSuccessStatus({
        field: "status",
      });
      setTimeout(() => {
        setSuccessStatus({});
      }, 1000);
    } catch (err) {
      setFailStatus({
        field: "status",
      });
      setIsLoading(false);
      setTimeout(() => {
        setFailStatus({});
      }, 1000);
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "product-requests");
      throw err;
    }
  };

  const handleCreateOrderFromPR = async (passedValues = null) => {
    try {
      setIsLoading(true);

      const targetValues = passedValues || {
        poNumber: orderModalData.poNumber,
        warehouseId: orderModalData.warehouseId,
        vendorId: orderModalData.vendorId,
        shipToAddress: orderModalData.shipToAddress,
        items: orderModalData.items,
      };

      if (!targetValues.poNumber || targetValues.poNumber.trim() === "") {
        setAlert({
          message: "PO Number is required",
          type: "danger",
          show: true,
          alertFor: "order-modal",
        });
        setIsLoading(false);
        return;
      }

      if (!targetValues.warehouseId) {
        setAlert({
          message: "Warehouse is required",
          type: "danger",
          show: true,
          alertFor: "order-modal",
        });
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
        setIsLoading(false);
        return;
      }

      if (!targetValues.vendorId) {
        setAlert({
          message: "Vendor is required",
          type: "danger",
          show: true,
          alertFor: "order-modal",
        });
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
        setIsLoading(false);
        return;
      }

      if (
        !targetValues.shipToAddress ||
        targetValues.shipToAddress.trim() === ""
      ) {
        setAlert({
          message: "Ship To Address is required",
          type: "danger",
          show: true,
          alertFor: "order-modal",
        });
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
        setIsLoading(false);
        return;
      }

      const selectedOrderItems = targetValues.items.filter(
        (item) => item.createOrder !== false,
      );

      if (selectedOrderItems.length === 0) {
        setAlert({
          message: "At least one item is required to create an order",
          type: "danger",
          show: true,
          alertFor: "order-modal",
        });
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
        setIsLoading(false);
        return;
      }

      const itemIds = selectedOrderItems
        .map((item) => item.id || item.productItemId)
        .filter((id) => id !== undefined && id !== null);

      if (itemIds.length === 0) {
        setAlert({
          message: "Items are missing. Please refresh and try again",
          type: "danger",
          show: true,
          alertFor: "order-modal",
        });
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
        setIsLoading(false);
        return;
      }

      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const orderResponse = await axios.put(
        `${url.backendURL}product-request/${pendingOrderPRId}/status/create-order`,
        {
          poNumber: targetValues.poNumber.trim(),
          orderWarehouseId: targetValues.warehouseId,
          vendorId: targetValues.vendorId,
          orderShipTo: targetValues.shipToAddress.trim(),
          itemIds: itemIds,
        },
        config,
      );

      setProductRequests((prevRequests) =>
        prevRequests.map((item) =>
          item.id === pendingOrderPRId ? { ...item, status: "approved" } : item,
        ),
      );

      setAlert({ show: false, alertFor: "", message: "", type: "" });
      setShowOrderModal(false);
      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      const successMessage =
        orderResponse.data.message || "Order created successfully";

      setPendingOrderPRId(null);
      setOrderModalData({
        poNumber: "",
        warehouseId: null,
        vendorId: null,
        shipToAddress: "",
        items: [],
      });

      const orderId = orderResponse.data.response?.data?.orderId;
      const canAccessPurchaseOrders =
        orderHasViewPermission ||
        orderHasCreatePermission ||
        orderHasUpdatePermission ||
        orderHasDeletePermission ||
        orderHasStatusPermission;

      if (canAccessPurchaseOrders) {
        setTimeout(() => {
          navigate("/dashboard/app/purchase-order-list", {
            state: {
              highlightedOrderId: orderId,
              showSuccessMessage: true,
              successMessage,
            },
          });
        }, 1500);
      } else {
        setAlert({
          message: successMessage,
          type: "success",
          show: true,
          alertFor: "product-requests",
        });
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 3000);
      }
    } catch (err) {
      let errorMessage = "An error occurred while creating the order";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setAlert({
        message: errorMessage,
        type: "danger",
        show: true,
        alertFor: "order-modal",
      });

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);

      // handleError(err, "product-requests");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductRequestDetails = async (productRequestId) => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}product-request/${productRequestId}/detail`,
        config,
      );
      if (response.data.response?.data?.products) {
        const products = response.data.response.data.products.map((item) => ({
          ...item,
          id: item.productItemId || item.id,
          createOrder: true,
        }));
        return products;
      }
      return [];
    } catch (error) {
      console.error("Error fetching product request details:", error);
      return [];
    }
  };

  const handleSubmitStatusNotes = async (option, productRequestId) => {
    if (option.value === "create_order") {
      setLoadingPRId(productRequestId);

      const selectedPR = productRequests.find(
        (pr) => pr.id === productRequestId,
      );
      if (selectedPR) {
        setPendingOrderPRId(productRequestId);

        try {
          const products = await fetchProductRequestDetails(productRequestId);
          const config = {
            headers: {
              Authorization: `${token}`,
            },
          };
          const detailResponse = await axios.get(
            `${url.backendURL}product-request/${productRequestId}/detail`,
            config,
          );
          const prDetail = detailResponse.data.response?.data;

          const prWarehouseId =
            prDetail?.warehouse_id || selectedPR?.warehouse_id || null;

          let defaultShipTo = prDetail?.ship_to || selectedPR?.ship_to || "";

          if (!defaultShipTo && prWarehouseId) {
            const prWarehouse = warehouseOptions.find(
              (w) => w.value === prWarehouseId,
            );
            defaultShipTo = prWarehouse?.shipTo || "";
          }

          setOrderModalData({
            poNumber: "",
            warehouseId: prWarehouseId,
            shipToAddress: defaultShipTo,
            items: products,
          });

          setShowOrderModal(true);
          setShowItemsInModal(true);
        } catch (error) {
          const fallbackWarehouseId =
            selectedPR?.warehouse_id || decodedToken?.warehouse_id || null;
          let fallbackShipTo = selectedPR?.ship_to || "";

          if (!fallbackShipTo && fallbackWarehouseId) {
            const fallbackWarehouse = warehouseOptions.find(
              (w) => w.value === fallbackWarehouseId,
            );
            fallbackShipTo = fallbackWarehouse?.shipTo || "";
          }

          setOrderModalData({
            poNumber: "",
            warehouseId: fallbackWarehouseId,
            shipToAddress: fallbackShipTo,
            items: products || [],
          });
          setShowOrderModal(true);
          setShowItemsInModal(true);
        } finally {
          setLoadingPRId(null);
        }
        return;
      } else {
        setLoadingPRId(null);
      }
    }
    handleSaveProductRequestFieldSelect(productRequestId, option.value)
      .then(() => {
        setSuccessStatus({ field: "status" });
        if (option.value === "approved") {
          if (alertRef.current) {
            alertRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
          productRequests.map((item) => {
            if (item.id === productRequestId) {
              item.status = "approved";
            }
          });
          setAlert({
            message: "Request status updated to approved successfully",
            type: "success",
            show: true,
            alertFor: "product-requests",
          });
          setTimeout(() => {
            setAlert({ show: false, alertFor: "", message: "", type: "" });
          }, 5000);
        }
        setShowModal(false);
        setIsLoading(false);
      })
      .catch(() => {
        setFailStatus({ field: "status" });
        setIsLoading(false);
      });
  };

  const handleClose = () => {
    setShowModal(false);
    setProductRequestId(null);
  };

  const handleCloseNotes = () => {
    setShowNotesModal(false);
    setSelectedProductRequest(null);
  };

  const handleSubmitNotes = (values, formikProps) => {
    setIsLoading(true);

    handleSaveProductRequestFieldSelect(
      productRequestId,
      "rejected",
      values.notes,
    )
      .then(() => {
        setSuccessStatus({ field: "status" });
        formikProps.setFieldValue("status", "rejected" || null);
        setShowModal(false);
        setIsLoading(false);
        productRequests.map((item) => {
          if (item.id === productRequestId) {
            item.status = "rejected";
            item.notes = values.notes;
          }
        });
        setAlert({
          message: "Request status updated to rejected successfully",
          type: "success",
          show: true,
          alertFor: "product-requests",
        });
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
      })
      .catch(() => {
        setFailStatus({ field: "status" });
        setIsLoading(false);
      });
  };

  const notesSchema = Yup.object().shape({
    notes: Yup.string().required("Notes is required"),
  });

  const getWarehouses = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}user/warehouse-list`,
        config,
      );

      const options = response.data.response.data.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
        shipTo: warehouse.address,
      }));
      setWarehouseOptions(options);
    } catch (err) {
      // handleError(err, "product-requests");
    }
  };

  const getVendors = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const response = await axios.get(`${url.backendURL}vendor/all`, config);

      const options = response.data.response.data.map((vendor) => ({
        value: vendor.id,
        label: vendor.name === "N/A" ? "No vendor" : vendor.name,
      }));
      setVendorOptions(options || []);
    } catch (err) {
      setVendorOptions([]);
    }
  };

  useEffect(() => {
    function toggleStickyNames() {
      const isWideScreen = window.innerWidth > 650;

      const stickyHeadName = document.querySelector(".sticky-head-name");
      const stickyDataNames = document.querySelectorAll(".sticky-data-names");

      if (isWideScreen) {
        stickyHeadName?.classList.add("sticky-action-head-buttons");
        stickyDataNames.forEach((data) =>
          data.classList.add("sticky-action-buttons"),
        );
      } else {
        stickyHeadName?.classList.remove("sticky-action-head-buttons");
        stickyDataNames.forEach((data) =>
          data.classList.remove("sticky-action-buttons"),
        );
      }
    }
    toggleStickyNames();
    window.addEventListener("resize", toggleStickyNames);
    return () => window.removeEventListener("resize", toggleStickyNames);
  }, []);

  return (
    <>
      <div>
        <style>
          {`
          .table-user .sticky-action-buttons {
            background-color: #ffffff !important;
            border: none;
            z-index: 2;
            height: auto;
          }
          .table-user .tr-user:hover .sticky-action-buttons {
            background-color: #f5f6fa !important;
          }
          .table-user .sticky-action-head-buttons {
            background: #e2e4e9 !important;
          }
          .table-user .sticky-action-head-buttons,
          .table-user .sticky-action-buttons {
            left: 0;
            z-index: 1;
            width: 180px;
            position: absolute;
            height: inherit;
            display: flex;
            align-items: center;
            padding-left: 15px;
          }
          .table-user .th-user:nth-child(2),
          .table .td-user:nth-child(2) {
            padding-left: 190px !important;
          }
           @media (max-width: 650px) {
            .table-user .sticky-action-head-buttons,
            .table-user .sticky-action-buttons {
               position: static !important;
               width: auto !important;
            }
            .table-user .th-user:nth-child(2),
            .table .td-user:nth-child(2) {
              padding-left: 0.75rem !important;
            }
          }
        `}
        </style>
        <div className="bd-example">
          <div className="d-flex justify-content-between flex-wrap pl-0">
            <Modal show={showModal} onHide={handleClose} centered>
              <Modal.Header className="custom-modal-header">
                <Modal.Title className="h6">
                  Change Status to Rejected
                </Modal.Title>
                <button
                  type="button"
                  className="btn-close-svg"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </Modal.Header>

              <Formik
                initialValues={{ notes: selectedProductRequest?.notes || "" }}
                validationSchema={notesSchema}
                onSubmit={(values, formikProps) =>
                  handleSubmitNotes(values, formikProps)
                }
              >
                {({ values, handleSubmit, errors, touched }) => (
                  <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Notes <span className="text-danger">*</span>
                        </Form.Label>
                        <Field
                          as="textarea"
                          name="notes"
                          value={values.notes}
                          className={`form-control ${
                            errors.notes && touched.notes ? "is-invalid" : ""
                          }`}
                          placeholder="Please provide notes for rejected the request"
                          rows={4}
                        />
                        <ErrorMessage
                          name="notes"
                          component="div"
                          className="invalid-feedback"
                        />
                      </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isLoading === "status"}
                      >
                        {isLoading === "status" ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button variant="danger" onClick={handleClose}>
                        Cancel
                      </Button>
                    </Modal.Footer>
                  </Form>
                )}
              </Formik>
            </Modal>

            <Modal show={showNotesModal} onHide={handleCloseNotes} centered>
              <Modal.Header className="custom-modal-header">
                <Modal.Title className="h6">Notes</Modal.Title>
                <button
                  type="button"
                  className="btn-close-svg"
                  onClick={handleCloseNotes}
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </Modal.Header>

              <Modal.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      type="text"
                      disabled={true}
                      style={{ backgroundColor: "#fff", color: "#8A92A6" }}
                      value={
                        selectedProductRequest?.status
                          ? selectedProductRequest.status
                              .charAt(0)
                              .toUpperCase() +
                            selectedProductRequest.status.slice(1).toLowerCase()
                          : ""
                      }
                      rows={4}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      type="text"
                      as="textarea"
                      value={selectedProductRequest?.notes}
                      disabled={true}
                      style={{ backgroundColor: "#fff", color: "#8A92A6" }}
                      rows={4}
                    />
                  </Form.Group>
                </Form>
              </Modal.Body>
            </Modal>

            <Modal
              show={showOrderModal}
              onHide={() => {
                setShowOrderModal(false);
                setPendingOrderPRId(null);
                setAlert({ show: false, alertFor: "", message: "", type: "" });
              }}
              size="xl"
              centered
            >
              <Modal.Header className="custom-modal-header">
                <Modal.Title>Order Create</Modal.Title>
                <button
                  type="button"
                  className="btn-close-svg"
                  onClick={() => {
                    setShowOrderModal(false);
                    setPendingOrderPRId(null);
                    setAlert({
                      show: false,
                      alertFor: "",
                      message: "",
                      type: "",
                    });
                  }}
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </Modal.Header>

              <Modal.Body style={{ padding: "1.5rem" }}>
                <Formik
                  key={pendingOrderPRId}
                  initialValues={{
                    poNumber: orderModalData.poNumber || "",
                    warehouseId: orderModalData.warehouseId || null,
                    vendorId: orderModalData.vendorId || null,
                    shipToAddress: orderModalData.shipToAddress || "",
                    items: orderModalData.items || [],
                  }}
                  validationSchema={orderValidationSchema}
                  enableReinitialize={false}
                  onSubmit={async (values, { setSubmitting, setTouched }) => {
                    setTouched({
                      poNumber: true,
                      warehouseId: true,
                      vendorId: true,
                      shipToAddress: true,
                      items: true,
                    });

                    await handleCreateOrderFromPR(values);
                    setSubmitting(false);
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    handleSubmit,
                    isSubmitting,
                    validateForm,
                    submitCount,
                    setFieldTouched,
                  }) => {
                    return (
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={12}>
                            <div
                              className={`d-flex items-center justify-center alert alert-${
                                orderModalData.items.length === 0
                                  ? "warning"
                                  : "success"
                              } alert-dismissible fade show`}
                              role="alert"
                              style={{ marginBottom: "1rem" }}
                            >
                              <strong>
                                {orderModalData.items.length === 0
                                  ? "Warning:"
                                  : "Note:"}
                              </strong>{" "}
                              {orderModalData.items.length === 0
                                ? "No products found for create order. Add at least one product minimum."
                                : "The product request status will be automatically set to approved when you create this order."}
                              <button
                                type="button"
                                className="btn-close"
                                onClick={(e) => {
                                  e.currentTarget.closest(".alert").remove();
                                }}
                                aria-label="Close"
                              ></button>
                            </div>
                          </Col>
                        </Row>

                        {alert.show && alert.alertFor === "order-modal" && (
                          <Row>
                            <Col md={12}>
                              <div
                                className="alert alert-danger alert-dismissible fade show"
                                role="alert"
                                style={{ marginBottom: "1rem" }}
                              >
                                {alert.message}
                                <button
                                  type="button"
                                  className="btn-close"
                                  onClick={() =>
                                    setAlert({
                                      show: false,
                                      alertFor: "",
                                      message: "",
                                      type: "",
                                    })
                                  }
                                  aria-label="Close"
                                ></button>
                              </div>
                            </Col>
                          </Row>
                        )}

                        <Row className="mb-3">
                          <Col md={6} lg={4}>
                            <Form.Group className="form-group">
                              <Form.Label>
                                PO Number <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="poNumber"
                                placeholder="Enter PO number"
                                value={values.poNumber}
                                onChange={(e) => {
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                                isInvalid={
                                  (touched.poNumber || submitCount > 0) &&
                                  !!errors.poNumber
                                }
                              />
                              {(touched.poNumber || submitCount > 0) &&
                                errors.poNumber && (
                                  <div className="text-danger mt-1">
                                    {errors.poNumber}
                                  </div>
                                )}
                            </Form.Group>
                          </Col>
                          <Col md={6} lg={4}>
                            <Form.Group className="form-group">
                              <Form.Label>
                                Vendor <span className="text-danger">*</span>
                              </Form.Label>
                              <Select
                                inputId="vendorId"
                                name="vendorId"
                                closeMenuOnScroll={(e) => {
                                  try {
                                    const target = e.target;

                                    if (
                                      !target ||
                                      !target.nodeType ||
                                      typeof target.closest !== "function"
                                    ) {
                                      return true;
                                    }

                                    const isMenuScroll =
                                      target.closest('[class*="MenuList"]') ||
                                      target.closest('[class*="menu"]');

                                    return !isMenuScroll;
                                  } catch (error) {
                                    return true;
                                  }
                                }}
                                options={vendorOptions}
                                value={vendorOptions.find(
                                  (option) => option.value === values.vendorId,
                                )}
                                onChange={(option) => {
                                  setFieldValue(
                                    "vendorId",
                                    option?.value || null,
                                  );
                                }}
                                onBlur={() => setFieldTouched("vendorId", true)}
                                noOptionsMessage={() => (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <span style={{ color: "#666" }}>
                                      No vendor found
                                    </span>
                                  </div>
                                )}
                                placeholder={
                                  vendorOptions.length === 0 ? (
                                    <span>No vendor found</span>
                                  ) : (
                                    "Select Vendor..."
                                  )
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuPlacement="auto"
                                styles={{
                                  option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isSelected
                                      ? "#007bff"
                                      : state.isFocused
                                        ? "#e6f7ff"
                                        : "#fff",
                                    color: "#333",
                                  }),
                                  control: (provided, state) => ({
                                    ...provided,
                                    borderColor:
                                      (touched.vendorId || submitCount > 0) &&
                                      errors.vendorId
                                        ? "#dc3545"
                                        : provided.borderColor,
                                    "&:hover": {
                                      borderColor:
                                        (touched.vendorId || submitCount > 0) &&
                                        errors.vendorId
                                          ? "#dc3545"
                                          : provided.borderColor,
                                    },
                                  }),
                                  menuPortal: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                  }),
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                  }),
                                }}
                              />
                              {(touched.vendorId || submitCount > 0) &&
                                errors.vendorId && (
                                  <div className="text-danger mt-1">
                                    {errors.vendorId}
                                  </div>
                                )}
                            </Form.Group>
                          </Col>
                          <Col md={6} lg={4}>
                            <Form.Group className="form-group">
                              <Form.Label>
                                Warehouse <span className="text-danger">*</span>
                              </Form.Label>
                              <Select
                                inputId="warehouseId"
                                name="warehouseId"
                                closeMenuOnScroll={(e) => {
                                  try {
                                    const target = e.target;

                                    if (
                                      !target ||
                                      !target.nodeType ||
                                      typeof target.closest !== "function"
                                    ) {
                                      return true;
                                    }

                                    const isMenuScroll =
                                      target.closest('[class*="MenuList"]') ||
                                      target.closest('[class*="menu"]');

                                    return !isMenuScroll;
                                  } catch (error) {
                                    return true;
                                  }
                                }}
                                options={warehouseOptions}
                                value={warehouseOptions.find(
                                  (option) =>
                                    option.value === values.warehouseId,
                                )}
                                onChange={(option) => {
                                  setFieldValue(
                                    "warehouseId",
                                    option?.value || null,
                                  );
                                  setFieldValue(
                                    "shipToAddress",
                                    option?.shipTo || values.shipToAddress,
                                  );
                                }}
                                onBlur={() =>
                                  setFieldTouched("warehouseId", true)
                                }
                                noOptionsMessage={() => (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <span style={{ color: "#666" }}>
                                      No warehouse found.
                                      {(warehouseHasCreatePermission ||
                                        decodedToken?.roles.some(
                                          (role) =>
                                            role.role_type ===
                                            roleTypes.SUPER_ADMIN,
                                        )) && (
                                        <span>
                                          {" "}
                                          <Link
                                            to="/dashboard/app/warehouse-add"
                                            style={{
                                              color: "#007bff",
                                              textDecoration: "none",
                                              cursor: "pointer",
                                            }}
                                          >
                                            Add new
                                          </Link>
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}
                                placeholder={
                                  warehouseOptions.length === 0 ? (
                                    <span>No warehouse found</span>
                                  ) : (
                                    "Select Warehouse..."
                                  )
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuPlacement="auto"
                                styles={{
                                  option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isSelected
                                      ? "#007bff"
                                      : state.isFocused
                                        ? "#e6f7ff"
                                        : "#fff",
                                    color: "#333",
                                  }),
                                  control: (provided, state) => ({
                                    ...provided,
                                    borderColor:
                                      (touched.warehouseId ||
                                        submitCount > 0) &&
                                      errors.warehouseId
                                        ? "#dc3545"
                                        : provided.borderColor,
                                    "&:hover": {
                                      borderColor:
                                        (touched.warehouseId ||
                                          submitCount > 0) &&
                                        errors.warehouseId
                                          ? "#dc3545"
                                          : provided.borderColor,
                                    },
                                  }),
                                  menuPortal: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                  }),
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                  }),
                                }}
                              />
                              {(touched.warehouseId || submitCount > 0) &&
                                errors.warehouseId && (
                                  <div className="text-danger mt-1">
                                    {errors.warehouseId}
                                  </div>
                                )}
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={12}>
                            <Form.Group className="form-group">
                              <Form.Label>
                                Requesting Warehouse Address{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                name="shipToAddress"
                                placeholder="Enter ship to address"
                                value={values.shipToAddress}
                                onChange={(e) => {
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                                isInvalid={
                                  (touched.shipToAddress || submitCount > 0) &&
                                  !!errors.shipToAddress
                                }
                                style={{
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "8px",
                                }}
                              />
                              {(touched.shipToAddress || submitCount > 0) &&
                                errors.shipToAddress && (
                                  <div className="text-danger mt-1">
                                    {errors.shipToAddress}
                                  </div>
                                )}
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mt-3">
                          <Col md={12}>
                            <div
                              className="d-flex justify-content-between align-items-center py-3 px-3"
                              style={{
                                backgroundColor: "#f8f9fa",
                                borderRadius: "8px",
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                              }}
                              onClick={() =>
                                setShowItemsInModal(!showItemsInModal)
                              }
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#e9ecef")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#f8f9fa")
                              }
                            >
                              <div>
                                <h6
                                  className="mb-0 text-primary"
                                  style={{
                                    fontWeight: "500",
                                    cursor: "pointer",
                                  }}
                                >
                                  Products ({orderModalData.items.length} items)
                                </h6>
                              </div>
                              <div
                                className="sidebar-toggle"
                                style={{
                                  position: "initial",
                                  cursor: "pointer",
                                }}
                              >
                                <i
                                  className="icon"
                                  style={{
                                    transform: showItemsInModal
                                      ? "rotate(90deg)"
                                      : "rotate(270deg)",
                                  }}
                                >
                                  <svg
                                    width="20"
                                    className="icon-20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M4.25 12.2744L19.25 12.2744"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    ></path>
                                    <path
                                      d="M10.2998 18.2988L4.2498 12.2748L10.2998 6.24976"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    ></path>
                                  </svg>
                                </i>
                              </div>
                            </div>

                            {showItemsInModal && (
                              <div
                                style={{
                                  maxHeight: "calc(75vh - 150px)",
                                  overflowY: "auto",
                                  overflowX: "auto",
                                }}
                              >
                                <Table
                                  id="bom-list-table datatable"
                                  className="overflow-auto mt-6"
                                  role="grid"
                                  responsive
                                  data-toggle="data-table"
                                  style={{
                                    width: "100%",
                                    marginTop: "1rem",
                                    overflow: "auto",
                                  }}
                                >
                                  <thead
                                    className="sticky-top"
                                    style={{
                                      position: "sticky",
                                      top: 0,
                                      zIndex: 10,
                                      backgroundColor: "#e2e4e9",
                                    }}
                                  >
                                    <tr
                                      style={{
                                        backgroundColor: "#e2e4e9",
                                      }}
                                    >
                                
                                      <th style={{ minWidth: "200px" }}>
                                        Quantity
                                      </th>
                                      <th style={{ minWidth: "200px" }}>
                                        Make
                                      </th>
                                      <th style={{ minWidth: "200px" }}>
                                        Model
                                      </th>
                                        <th style={{ minWidth: "200px" }}>
                                        Product
                                      </th>
                                      <th style={{ minWidth: "200px" }}>SKU</th>
                                      <th style={{ minWidth: "200px" }}>
                                        Rate
                                      </th>
                                      <th style={{ minWidth: "200px" }}>
                                        Amount
                                      </th>
                                      <th style={{ minWidth: "200px" }}>Job</th>
                                      <th style={{ minWidth: "200px" }}>
                                        System
                                      </th>
                                      <th style={{ minWidth: "200px" }}>
                                        Phase
                                      </th>
                                      <th style={{ minWidth: "150px" }}>
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(() => {
                                      const filteredItems =
                                        orderModalData.items.filter(
                                          (item) => item.createOrder !== false,
                                        );
                                      if (filteredItems.length === 0) {
                                        return (
                                          <tr>
                                            <div
                                              style={{
                                                position: "sticky",
                                                bottom: "0",
                                                left: "25%",
                                                width: "100%",
                                                display: "flex",
                                                justifyContent: "center",
                                                backgroundColor: "white",
                                                padding: "1rem 0",
                                                zIndex: 10,
                                              }}
                                            >
                                              No products found for create
                                              order. Add at least one product
                                              minimum.
                                            </div>
                                          </tr>
                                        );
                                      }
                                      return filteredItems.map(
                                        (item, filteredIndex) => {
                                          const originalIndex =
                                            orderModalData.items.findIndex(
                                              (i) => i === item,
                                            );
                                          const selectedCount =
                                            filteredItems.length;
                                          const isLastItem =
                                            selectedCount === 1;

                                          return (
                                            <tr key={originalIndex}>
                                           
                                              <td style={{ width: "200px" }}>
                                                {item.quantity}
                                              </td>
                                              <td style={{ width: "200px" }}>
                                                {item.manufacturerName || "N/A"}
                                              </td>
                                              <td style={{ width: "200px" }}>
                                                {item.model || "N/A"}
                                              </td>
                                              <td style={{ width: "200px" }}>
                                                {item.productName}
                                              </td>
                                              <td style={{ width: "200px" }}>
                                                {item.sku || "N/A"}
                                              </td>
                                              <td style={{ width: "200px" }}>
                                                {item.rate || 0}
                                              </td>
                                              <td style={{ width: "200px" }}>
                                                {item.amount || 0}
                                              </td>
                                              <td style={{ width: "200px" }}>
                                                {item.jobName ||
                                                  "No Job Assigned"}
                                              </td>
                                              <td style={{ width: "200px" }}>
                                                {item.systemName || "N/A"}
                                              </td>
                                              <td style={{ width: "200px" }}>
                                                {item.phaseName || "N/A"}
                                              </td>
                                              <td
                                                style={{
                                                  width: "150px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={
                                                    <Tooltip
                                                      id={`tooltip-action-${originalIndex}`}
                                                      className="custom-contact-tooltip"
                                                    >
                                                      {isLastItem
                                                        ? "At least one item is required for order"
                                                        : "Click to exclude from order"}
                                                    </Tooltip>
                                                  }
                                                >
                                                  <span
                                                    style={{
                                                      cursor: isLastItem
                                                        ? "not-allowed"
                                                        : "pointer",
                                                      display: "inline-block",
                                                    }}
                                                  >
                                                    <IconButton
                                                      size="medium"
                                                      color="error"
                                                      onClick={() => {
                                                        if (isLastItem) {
                                                          return;
                                                        }
                                                        const updatedItems = [
                                                          ...values.items,
                                                        ];
                                                        updatedItems[
                                                          originalIndex
                                                        ] = {
                                                          ...updatedItems[
                                                            originalIndex
                                                          ],
                                                          createOrder: false,
                                                        };
                                                        setFieldValue(
                                                          "items",
                                                          updatedItems,
                                                          true,
                                                        );
                                                      }}
                                                      disabled={isLastItem}
                                                      style={{
                                                        cursor: isLastItem
                                                          ? "not-allowed"
                                                          : "pointer",
                                                        opacity: isLastItem
                                                          ? 0.3
                                                          : 1,
                                                      }}
                                                    >
                                                      <CancelIcon fontSize="medium" />
                                                    </IconButton>
                                                  </span>
                                                </OverlayTrigger>
                                              </td>
                                            </tr>
                                          );
                                        },
                                      );
                                    })()}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </Col>
                        </Row>

                        {(touched.items || submitCount > 0) && errors.items && (
                          <Row className="mb-3">
                            <Col md={12}>
                              <div className="text-danger">
                                {typeof errors.items === "string"
                                  ? errors.items
                                  : "At least one item must be selected for order"}
                              </div>
                            </Col>
                          </Row>
                        )}

                        <Modal.Footer className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={
                              isLoading ||
                              isSubmitting ||
                              values.items.filter(
                                (item) => item.createOrder !== false,
                              ).length === 0
                            }
                          >
                            {isLoading ? "Creating Order..." : "Create Order"}
                          </Button>
                          <Button
                            variant="danger"
                            type="button"
                            onClick={() => {
                              setShowOrderModal(false);
                              setPendingOrderPRId(null);
                            }}
                            disabled={isLoading}
                          >
                            Cancel
                          </Button>
                        </Modal.Footer>
                      </Form>
                    );
                  }}
                </Formik>
              </Modal.Body>
            </Modal>
          </div>
        </div>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header className="d-flex justify-content-between listing-card-header">
                <div className="header-title">
                  <h4 className="card-title">Product Request List</h4>
                </div>
              </Card.Header>
              <div className="d-flex align-items-center justify-content-between">
                <Row className="align-items-center d-flex justify-content-between mt-2 w-100">
                  {/* Show entries section */}
                  <Col
                    sm="5"
                    md="4"
                    lg="4"
                    xs="12"
                    ref={alertRef}
                    className="order-1"
                  >
                    <div className="pt-4 d-flex align-items-center col-12 ms-4 listing-entries">
                      <label className="me-2">Show</label>
                      <select
                        className="form-select py-1 ps-2 pe-0 text-sm bg-white rounded-lg w-25"
                        value={entriesPerPage}
                        onChange={handleEntriesPerPageChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                      <span className="ms-2">entries</span>
                    </div>
                  </Col>

                  <Col sm="7" md="8" lg="8" xs="12" className="order-2">
                    <div className="pt-4 d-flex justify-content-start align-items-center">
                      <Row className="g-2 ms-4 ms-sm-0 align-items-end w-100 justify-content-end listing-controls-row">
                        {/* Search Input */}
                        <Col
                          xs="12"
                          sm="auto"
                          md="auto"
                          lg="auto"
                          className="d-flex ps-0 me-0 me-sm-0 flex-column flex-md-row align-items-start align-items-md-center listing-search-col"
                        >
                          <label className="me-2 mb-1 mb-md-0 text-nowrap">
                            Search:
                          </label>
                          <div
                            style={{ maxWidth: "180px", width: "100%" }}
                            className="listing-search-input-wrapper pe-sm-0 pe-3"
                          >
                            <SearchInput
                              searchQuery={searchQuery}
                              setSearchQuery={setSearchQuery}
                              loading={loading}
                              setLoading={setLoading}
                            />
                          </div>
                        </Col>

                        {hasCreatePermission &&
                          decodedToken?.roles?.some(
                            (role) => role.role_type !== roleTypes.SUPER_ADMIN,
                          ) && (
                            <Col
                              xs="12"
                              sm="auto"
                              md="auto"
                              lg="auto"
                              className="d-flex justify-content-start justify-content-md-end align-items-center ps-0 ps-lg-4 listing-add-button-col"
                            >
                              <Link
                                to={`/dashboard/app/product-request-add`}
                                className="mt-1 mt-md-0"
                              >
                                <Button
                                  type="submit"
                                  variant="btn btn-primary"
                                  className=" responsive-button "
                                >
                                  Add Product Request
                                </Button>
                              </Link>
                            </Col>
                          )}
                      </Row>
                    </div>
                  </Col>
                </Row>
              </div>

              <Card.Body className="px-0">
                {alert.alertFor === "product-requests" && (
                  <Row>
                    <Col className="mt-2 mb-2 ms-4 me-4">
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show && alert.alertFor !== "order-modal"}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />
                    </Col>{" "}
                  </Row>
                )}

                <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table
                    id="category-list-table datatable"
                    className="table-user"
                    role="grid"
                    responsive
                    data-toggle="data-table"
                    style={{ minWidth: "1200px" }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#e2e4e9" }} className="tr-user">
                        <th className="sticky-head-name th-user" style={{ minWidth: "15rem" }}>Warehouse</th>

                        <th className="th-user" style={{ minWidth: "20rem" }}>
                          Requesting Warehouse
                        </th>
                        <th className="th-user" style={{ minWidth: "12rem" }}>Date</th>
                        <th className="th-user" style={{ minWidth: "15rem" }}>Status</th>
                        <th className="th-user" style={{ minWidth: "12rem" }}>Action</th>
                      </tr>
                    </thead>
                  {!dataLoader && productRequests.length > 0 ? (
                    <tbody>
                      {productRequests?.map((item, idx) => (
                        <tr
                          key={idx}
                          className="table-hover tr-user"
                          data-request-id={item.id}
                          style={{
                            backgroundColor:
                              highlightedRequestId === item.id
                                ? "#d0deefe6"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                        >
                          <td className="sticky-data-names td-user">{item?.warehouse_name}</td>
                          <td className="td-user">{item?.ship_to}</td>
                          <td className="td-user">
                            {item?.date
                              ? new Date(item.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                  },
                                )
                              : ""}
                          </td>

                          <td className="text-capitalize td-user">
                            <div
                              title={item?.status}
                              style={{
                                position: "relative",
                                display: "inline-block",
                                width: "100%",
                              }}
                            >
                              <div style={{ position: "relative" }}>
                                <Select
                                  closeMenuOnScroll={(e) => {
                                    try {
                                      const target = e.target;

                                      if (
                                        !target ||
                                        !target.nodeType ||
                                        typeof target.closest !== "function"
                                      ) {
                                        return true;
                                      }

                                      const isMenuScroll =
                                        target.closest('[class*="MenuList"]') ||
                                        target.closest('[class*="menu"]');

                                      return !isMenuScroll;
                                    } catch (error) {
                                      return true;
                                    }
                                  }}
                                  name="status"
                                  isDisabled={
                                    !hasStatusPermission ||
                                    decodedToken?.roles?.some(
                                      (role) =>
                                        role.role_type ===
                                        roleTypes.SUPER_ADMIN,
                                    ) ||
                                    item?.status !== "pending" ||
                                    loadingPRId === item?.id
                                  }
                                  isLoading={loadingPRId === item?.id}
                                  options={statusOptionsFormatted}
                                  value={statusOptionsFormatted?.find(
                                    (option) => option.value === item?.status,
                                  )}
                                  onChange={async (e) => {
                                    if (e?.value === item?.status) {
                                      return;
                                    } else if (e?.value === "rejected") {
                                      setShowModal(true);
                                      setProductRequestId(item?.id);
                                      setSelectedProductRequest(item);
                                    } else if (e?.value === "create_order") {
                                      await getWarehouses();
                                      await getVendors();
                                      handleSubmitStatusNotes(e, item?.id);
                                    } else if (
                                      e?.value === "pending" ||
                                      e?.value === "approved"
                                    ) {
                                      setSelectedProductRequest(item);
                                      setIsLoading(true);
                                      setProductRequestId(item?.id);
                                      handleSubmitStatusNotes(e, item?.id);
                                    }
                                  }}
                                  isClearable
                                  placeholder="Select Status"
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  menuPlacement="auto"
                                  styles={{
                                    container: (provided) => ({
                                      ...provided,
                                      width: "100%",

                                      backgroundColor:
                                        !hasStatusPermission && "white",
                                    }),
                                    control: (provided) => ({
                                      ...provided,
                                      color: "#5e667b",
                                      backgroundColor:
                                        !hasStatusPermission && "white",
                                      width: "100%",


                                      borderColor:
                                        successStatus?.field === "status" &&
                                        productRequestId === item?.id
                                          ? "green"
                                          : failStatus?.field === "status" &&
                                            productRequestId === item?.id &&
                                            "red",
                                      boxShadow:
                                        successStatus?.field === "status" &&
                                        productRequestId === item?.id
                                          ? "0 0 5px green"
                                          : failStatus.field === "status" &&
                                            productRequestId === item?.id &&
                                            "0 0 5px red",
                                    }),
                                    option: (provided, state) => ({
                                      ...provided,
                                      backgroundColor: state.isSelected
                                        ? "#007bff"
                                        : state.isFocused
                                          ? "#e6f7ff"
                                          : !hasStatusPermission
                                            ? "white"
                                            : "#fff",
                                      color: "#333",
                                      width: "100%",

                                    }),
                                    menuPortal: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,

                                    }),
                                    menu: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,

                                    }),
                                  }}
                                />

                                {isLoading && productRequestId === item?.id && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "40%",
                                      right: "50%",
                                      transform: "translateY(-50%)",
                                      pointerEvents: "none",
                                    }}
                                  >
                                    <RotatingLines
                                      strokeWidth="5"
                                      animationDuration="0.75"
                                      width="20"
                                      strokeColor="#b2b4b9"
                                      color="#b2b4b9"
                                      visible={true}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          <td style={{ textWrap: "unset" }} className="td-user">
                            <div className="flex align-items-center list-user-action">
                              {(hasViewPermission ||
                                decodedToken?.roles.some(
                                  (role) =>
                                    role.role_type === roleTypes.SUPER_ADMIN,
                                )) && (
                                <>
                                  {" "}
                                  <Link
                                    className={`btn btn-sm btn-icon btn-success`}
                                    data-toggle="tooltip"
                                    data-placement="top"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(
                                        `/dashboard/app/product-request-view/${item.id}`,
                                      );
                                    }}
                                    title="View"
                                    data-original-title="View"
                                  >
                                    <span className="btn-inner">
                                      <IoEyeOutline
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      />
                                    </span>
                                  </Link>{" "}
                                </>
                              )}
                              {hasUpdatePermission &&
                                decodedToken?.roles.some(
                                  (role) =>
                                    role.role_type !== roleTypes.SUPER_ADMIN,
                                ) && (
                                  <>
                                    {" "}
                                    <Link
                                      className={`btn btn-sm btn-icon btn-warning 
  ${item?.status === "pending" ? "opacity-100" : "opacity-50 disabled"}
  `}
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      title="Edit"
                                      data-original-title="Edit"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (item?.status === "pending") {
                                          navigate(
                                            `/dashboard/app/product-request-edit/${item.id}`,
                                          );
                                        }
                                      }}
                                    >
                                      <span className="btn-inner">
                                        <svg
                                          width="20"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                          <path
                                            d="M15.1655 4.60254L19.7315 9.16854"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                        </svg>
                                      </span>
                                    </Link>{" "}
                                  </>
                                )}
                              {hasDeletePermission &&
                                decodedToken?.roles.some(
                                  (role) =>
                                    role.role_type !== roleTypes.SUPER_ADMIN,
                                ) && (
                                  <OverlayTrigger
                                    trigger="click"
                                    show={showPopover === `delete-${idx}`}
                                    onToggle={() =>
                                      togglePopover(`delete-${idx}`)
                                    }
                                    placement="top"
                                    overlay={
                                      <Popover id={`popover-delete-${idx}`}>
                                        <Popover.Header as="h2">
                                          <strong>Delete</strong>{" "}
                                          <span>{`(${item.warehouse_name})`}</span>
                                          ?
                                        </Popover.Header>
                                        <Popover.Body>
                                          Once deleted, this action cannot be
                                          undone.
                                          <div className="d-flex justify-content-end mt-2">
                                            <Button
                                              size="sm"
                                              variant="danger"
                                              className="me-2"
                                              onClick={(e) => {
                                                if (deleteIsSubmit) {
                                                  setDeleteIsSubmit(false);
                                                  handleDeleteProductRequest(
                                                    e,
                                                    item.id,
                                                  );
                                                }
                                              }}
                                            >
                                              Delete
                                            </Button>
                                            <Button
                                              size="sm"
                                              className="text-black bg-white"
                                              variant="secondary"
                                              onClick={() =>
                                                togglePopover(`delete-${idx}`)
                                              }
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        </Popover.Body>
                                      </Popover>
                                    }
                                  >
                                    <Link
                                      className={`btn btn-sm btn-icon btn-danger ${
                                        item?.status !== "pending"
                                          ? "opacity-50 disabled"
                                          : "opacity-100"
                                      }`}
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      title="Delete"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (item?.status === "pending") {
                                          setSelectedData({
                                            warehouseName: item?.warehouse_name,
                                            id: item?.id,
                                          });
                                        }
                                      }}
                                    >
                                      <span className="btn-inner">
                                        <svg
                                          width="20"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                          stroke="currentColor"
                                        >
                                          <path
                                            d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                          <path
                                            d="M20.708 6.23975H3.75"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                          <path
                                            d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                        </svg>
                                      </span>
                                    </Link>
                                  </OverlayTrigger>
                                )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : dataLoader ? (
                    <tbody>
                      <tr>
                        <td colSpan={5}>
                          <FadeLoaderComponent />
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={5}>
                          <div className="d-flex w-100 justify-content-center">
                            <p>No product requests found</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )}
                </Table>
              </div>

                <Row className="align-items-center mt-2">
                  <Col sm="6">
                    <div className="d-flex flex-wrap items-center ms-4">
                      <div className="">
                        Showing{" "}
                        {Math.min(
                          (currentPage - 1) * entriesPerPage + 1,
                          totalEntries,
                        )}{" "}
                        to{" "}
                        {Math.min(currentPage * entriesPerPage, totalEntries)}{" "}
                        of {totalEntries} entries
                      </div>
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="bd-example d-flex justify-content-center justify-content-md-end me-4">
                      <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default RequestProductList;
